import React, { useEffect, useMemo, useState } from "react";
import classes from "./Packages.module.css";
import headerClasses from "../Home/Home.module.css";
import ConvertedPackages from "./convertedPackages";
import DatePricingBar from "./DatePricingBar";
import { useBookedDates } from "./useBookedDates";
import {
  FALLBACK_DATE,
  PackageId,
  pickDefaultDate,
  resolveDate,
} from "./pricing";
import QuoteModal from "./QuoteModal";

const Packages = () => {
  // One date: the event day, which is what the package rates key off. Multi-day
  // stays are priced in the quote modal, which carries its own range picker.
  const [date, setDate] = useState<Date>(FALLBACK_DATE);
  const [pickedByVisitor, setPickedByVisitor] = useState(false);
  const [quoteFor, setQuoteFor] = useState<{
    pkg: PackageId;
    name: string;
  } | null>(null);
  const { availabilityFor, isBooked, loaded } = useBookedDates();

  // Captured once so a date sitting on the boundary can't flip between renders.
  const today = useMemo(() => new Date(), []);
  const resolution = useMemo(() => resolveDate(date, today), [date, today]);

  // Availability arrives after first paint, so the default is refined once the
  // calendar loads — unless the visitor has already chosen a date themselves.
  useEffect(() => {
    if (!loaded || pickedByVisitor) return;
    setDate(pickDefaultDate(isBooked, today));
    // `isBooked` closes over freshly-loaded bookings; `loaded` is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, pickedByVisitor, today]);

  const handleDateChange = (next: Date) => {
    setDate(next);
    setPickedByVisitor(true);
  };

  // Booked days — and the winter closure, which rides along in the same list —
  // are greyed out in the picker rather than merely reported after the fact.
  const isDateSelectable = (candidate: Date) => !isBooked(candidate);

  return (
    <div className={classes.packages}>
      <div className={classes.headerBackdrop}>
        <div className={headerClasses.header}>
          <h1>PACKAGES</h1>
        </div>
      </div>
      {/* Lives outside ConvertedPackages: that component's wrapper sets
          `overflow: hidden`, which would stop `position: sticky` working. */}
      <DatePricingBar
        date={date}
        onDateChange={handleDateChange}
        resolution={resolution}
        availability={availabilityFor(date)}
        minDate={today}
        isDateSelectable={isDateSelectable}
      />
      <ConvertedPackages
        resolution={resolution}
        onGetQuote={(pkg, name) => setQuoteFor({ pkg, name })}
      />
      {quoteFor && (
        <QuoteModal
          pkg={quoteFor.pkg}
          packageName={quoteFor.name}
          eventDate={date}
          today={today}
          isDateSelectable={isDateSelectable}
          isBooked={isBooked}
          onClose={() => setQuoteFor(null)}
        />
      )}
    </div>
  );
};

export default Packages;
